import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useNavigate, useLocation } from 'react-router-dom';
import { toast } from 'sonner';
import { Search, Users, ClipboardCheck, Home as HomeIcon, Briefcase, HardHat, TrendingUp, LogOut, Menu, X, Command, MapPin, Shield, Database, ShieldCheck, ShieldAlert, Upload, PhoneCall, FileText, Calendar, Settings, Package, BarChart3, GanttChart, Activity, Layers, Minus, ChevronLeft, ChevronRight, Building2, ClipboardList, GitBranch, DollarSign, Receipt, HelpCircle, BookOpen, Eye, Truck, FileSignature, Palette, Wrench, Paintbrush, ScrollText, Globe, Trash2, Ruler, Undo2, HardDrive, CalendarClock } from 'lucide-react';
import HelpPanel from '@/components/HelpPanel';
import DuplicatePropertyDialog from '@/components/DuplicatePropertyDialog';
import NotificationBell from '@/components/NotificationBell';
import WalkthroughRecorderBanner from '@/components/WalkthroughRecorderBanner';
import { DriveRenamePrompt } from '@/components/drive/DriveRenamePrompt';

const NAV_ITEMS = [
  { path: '/', label: 'Dashboard', icon: HomeIcon },
  { path: '/update-resend', label: 'Update & Resend', icon: Undo2 },
  { path: '/workflow/start', label: 'Workflow Runner', icon: Activity },
  { path: '/walkthroughs', label: 'Walkthrough Runner', icon: Activity },
  { path: '/settings/status-workflows', label: 'Status Workflows', icon: Layers },
  { path: '/pipeline', label: 'Pipeline Hub', icon: GitBranch },
  { path: '/documents', label: 'Documents', icon: FileText },
  { type: 'divider', label: 'CRM' },
  { path: '/clients', label: 'Clients', icon: Users },
  { path: '/properties', label: 'Properties', icon: MapPin },
  { path: '/agents', label: 'Agents', icon: Building2 },
  { path: '/leads', label: 'Leads', icon: TrendingUp },
  { path: '/web-leads', label: 'Website Enquiries', icon: Globe },
  { path: '/reconnect', label: 'Reconnect Clients', icon: Undo2 },
  { path: '/maintenance-plans', label: 'Maintenance Plans', icon: CalendarClock },
  { path: '/day-planner', label: 'Day Planner', icon: Calendar },
  { path: '/production-notes', label: 'Production Notes', icon: BookOpen },
  { type: 'divider', label: 'Jobs' },
  { path: '/appraisals', label: 'Appraisals', icon: ClipboardCheck },
  { path: '/legacy-evaluation', label: 'Legacy Evaluation', icon: ShieldAlert },
  { path: '/calendar', label: 'Calendar', icon: Calendar },
  { path: '/schedule', label: 'Work Booking Grid', icon: GanttChart },
  { path: '/gantt', label: 'Job Schedule', icon: GanttChart },
  { path: '/contractors', label: 'Contractors', icon: HardHat },
  { path: '/contractor-reports', label: 'Contractor Reports', icon: FileSignature },
  { path: '/invoices', label: 'Invoices', icon: FileText },
  { path: '/expenses', label: 'Expenses', icon: Receipt },
  { type: 'divider', label: 'Operations' },
  { path: '/roof-report', label: 'Roof Report', icon: ClipboardList },
  { path: '/health-check', label: 'Roof Health Check', icon: Activity },
  { path: '/territory', label: 'Call Territories', icon: MapPin },
  { path: '/materials', label: 'Materials', icon: Package },
  { path: '/suppliers', label: 'Suppliers', icon: Building2 },
  { path: '/orders', label: 'Orders', icon: Truck },
  { path: '/component-rates', label: 'Rate Tables', icon: Layers },
  { path: '/catalogue-audit', label: '↳ Catalogue Audit', icon: ClipboardCheck },
  { path: '/inventory', label: 'Inventory', icon: Package },
  { path: '/insurance', label: 'Insurance', icon: ShieldCheck },
  { path: '/telemarketing', label: 'Telemarketing', icon: PhoneCall },
  { path: '/letters', label: 'Letters', icon: FileText },
  { path: '/communication-scripts', label: 'Comm Scripts', icon: FileText },
  { path: '/condition-scripts', label: 'Condition Scripts', icon: FileText },
  { type: 'divider', label: 'Config' },
  { path: '/condition-script-matrix', label: 'Comment ↔ Roof Matrix', icon: GitBranch },
  { path: '/estimate-templates', label: 'Estimate Templates', icon: FileSignature },
  { path: '/descriptions', label: 'Descriptions', icon: FileText },
  { path: '/item-requirements', label: 'Item Requirements', icon: ClipboardList },
  { path: '/roof-type-materials', label: 'Roof Type Materials', icon: GitBranch },
  { path: '/product-roof-types', label: 'Product Roof Types', icon: Package },
  { path: '/dropdown-manager', label: 'Dropdown Options', icon: Settings },
  { path: '/report-setup', label: 'Report Setup', icon: FileText },
  { path: '/report-setup?tab=pdf_styling', label: 'PDF Styling', icon: Palette },
  { path: '/script-audit', label: 'Script Audit Studio', icon: ScrollText },
  { path: '/token-registry', label: '↳ Token Registry', icon: ScrollText },
  // Estimate Rates page retired 2026-02 — rates now live in Estimate Templates.
  { path: '/roof-conditions', label: 'Roof Conditions', icon: ClipboardList },
  { path: '/appraisal-conditions', label: 'Appraisal Conditions', icon: FileText },
  { path: '/task-catalog', label: 'Task Catalog', icon: ClipboardList },
  { type: 'divider', label: 'Reports' },
  { path: '/reports-library', label: 'Reports Library', icon: BookOpen },
  { path: '/reports/variant-insights', label: 'Variant Insights', icon: BarChart3 },
  { path: '/roof-templates', label: 'Roof Templates', icon: HomeIcon },
  { path: '/flashing-templates', label: 'Flashing Templates', icon: Minus },
  { path: '/procedure-templates', label: 'Procedure Templates', icon: FileText },
  { path: '/financials', label: 'Financials', icon: BarChart3 },
  { path: '/audit-log', label: 'Audit Log', icon: Activity, adminOnly: true },
  { type: 'divider', label: 'Admin' },
  { path: '/admin/approvals', label: 'Pending Approvals', icon: ShieldCheck, adminOnly: true },
  { path: '/admin/roof-details-dropdowns', label: 'Roof Details Dropdowns', icon: Layers, adminOnly: true },
  { path: '/admin/spouting-consumables', label: 'Spouting Consumables', icon: Wrench, adminOnly: true },
  { path: '/admin/materials-audit', label: 'Materials Audit', icon: ClipboardCheck, adminOnly: true },
  { path: '/admin/suppliers', label: 'Suppliers', icon: Truck, adminOnly: true },
  { path: '/bulk', label: 'Bulk Ops', icon: Layers, adminOnly: true },
  { path: '/settings', label: 'Settings', icon: Settings, adminOnly: true },
  { path: '/duplicate-clients', label: 'Duplicate Clients', icon: Users, adminOnly: true },
  { path: '/admin/quote-maths-check', label: 'Quote Maths Check', icon: ShieldAlert, adminOnly: true },
  { path: '/admin/migrations', label: 'Migrations', icon: ShieldAlert, adminOnly: true },
  { path: '/admin/bullet-groups', label: 'Quote Bullets', icon: ClipboardList, adminOnly: true },
  { path: '/admin/fixings', label: 'Fixings', icon: ClipboardList, adminOnly: true },
  { path: '/admin/orphan-sweep', label: 'Orphan Sweep', icon: ShieldAlert, adminOnly: true },
  { path: '/admin/test-data-cleanup', label: 'Test Data Cleanup', icon: Trash2, adminOnly: true },
  { path: '/admin/property-duplicates', label: 'Property Duplicates', icon: HomeIcon, adminOnly: true },
  { path: '/admin/suburb-area-codes', label: 'Suburb Area Codes', icon: MapPin, adminOnly: true },
  { path: '/admin/id-chip-theme', label: 'ID Chip Theme', icon: Palette, adminOnly: true },
  { path: '/admin/pdf-filename-templates', label: 'PDF Filename Templates', icon: FileText, adminOnly: true },
  { path: '/admin/extra-painting-items', label: 'Extra Painting Items', icon: Paintbrush, adminOnly: true },
  { path: '/admin/paint-systems', label: 'Paint Systems', icon: Palette, adminOnly: true },
  { path: '/admin/roof-m2-calibration', label: 'Roof m² Calibration', icon: Ruler, adminOnly: true },
  { path: '/admin/stock-bulk-in', label: 'Stock Bulk-In', icon: Truck, adminOnly: true },
  { path: '/admin/job-return', label: 'Job Return', icon: Undo2, adminOnly: true },
  { path: '/users', label: 'Users', icon: Shield, adminOnly: true },
  { path: '/admin/google-drive', label: 'Google Drive', icon: HardDrive, adminOnly: true },
  { path: '/admin/documents-drive', label: 'Documents & Drive', icon: HardDrive, adminOnly: true },
  { path: '/admin/job-model', label: 'Job + Quotes Model', icon: BarChart3, adminOnly: true },
  { path: '/admin/security', label: 'Security & Devices', icon: ShieldAlert, adminOnly: true },
  { path: '/data-sync', label: 'Data Sync', icon: Database, adminOnly: true, masterOnly: true },
  { path: '/migration', label: 'Migration', icon: Upload, adminOnly: true, masterOnly: true },
  { path: '/franchise-manager', label: 'Franchises', icon: Building2, adminOnly: true, masterOnly: true },
  { path: '/user-manual', label: 'User Manual', icon: BookOpen, adminOnly: true },
];

export default function AppLayout({ children }) {
  const { user, logout, api } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  // iter303ak-fix4 · Embed bypass — flag only; the early-return is at the
  // bottom to keep hook ordering stable (rules of hooks).
  const embedParam = new URLSearchParams(location.search).get('embed') || '';
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const searchRef = useRef(null);
  const currentPath = location.pathname;
  const currentSearch = location.search;
  const fullPath = currentPath + currentSearch;

  useEffect(() => {
    const handler = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setSearchOpen(true);
      }
      if (e.key === 'Escape') setSearchOpen(false);
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, []);

  useEffect(() => {
    if (searchOpen && searchRef.current) searchRef.current.focus();
  }, [searchOpen]);

  const doSearch = useCallback(async (q) => {
    setSearchQuery(q);
    if (q.length < 2) { setSearchResults([]); return; }
    try {
      const { data } = await api.get(`/search?q=${encodeURIComponent(q)}`);
      // Backend returns {clients:[], properties:[], appraisals:[], leads:[]}.
      // Flatten into a single list with type/id/display fields.
      const flat = [];
      const sections = [
        { key: 'clients', type: 'client', labelFields: (r) => r.display || `${r.name_first || ''} ${r.name_last || ''}`.trim() || r.company_name || 'Client', subFields: (r) => r.email || r.phone_mobile || r.phone_home || '' },
        { key: 'properties', type: 'property', labelFields: (r) => r.display || `${r.property_address_num || ''} ${r.property_address_street || ''}`.trim() || 'Property', subFields: (r) => r.property_address_suburb || '' },
        { key: 'appraisals', type: 'appraisal', labelFields: (r) => r.display || `Appraisal #${r.appraisal_no || r.access_id || r._id?.slice(-6)}`, subFields: (r) => r.quote_type_ref || r.appraisal_status || '' },
        { key: 'leads', type: 'lead', labelFields: (r) => r.display || r.lead_name || 'Lead', subFields: (r) => r.lead_source || '' },
      ];
      for (const s of sections) {
        for (const r of (data?.[s.key] || [])) {
          flat.push({ type: s.type, id: r._id, title: s.labelFields(r), subtitle: s.subFields(r) });
        }
      }
      setSearchResults(flat);
    } catch { setSearchResults([]); }
  }, [api]);

  const goToResult = (r) => {
    setSearchOpen(false);
    setSearchQuery('');
    setSearchResults([]);
    const paths = { client: '/clients', property: '/properties', appraisal: '/appraisals', lead: '/leads' };
    navigate(`${paths[r.type] || '/'}/${r.id}`);
  };

  const typeIcons = { client: Users, property: MapPin, appraisal: ClipboardCheck, lead: TrendingUp };

  const isFranchise = user?.is_franchise;
  const isViewingFranchise = user?.viewing_franchise;

  // iter303ak-fix4 · Embed short-circuit — render just the children with
  // no sidebar / no top nav / no search / no help. AppraisalDetailPage's
  // own embed branch supplies the slim header and the "Save & Return
  // to Runner" button. All hooks above have run so hook ordering is stable.
  if (embedParam) {
    // Focused pop-outs: block links that would wander to other records.
    const lockLinks = (e) => {
      const a = e.target.closest && e.target.closest('a[href]');
      if (!a || a.target === '_blank') return;
      const url = new URL(a.getAttribute('href'), window.location.origin);
      if (url.origin !== window.location.origin || url.pathname === location.pathname) return;
      e.preventDefault(); e.stopPropagation();
      toast.message('Finish here, then use Save & Return.');
    };
    return <div data-testid="app-layout-embed" onClickCapture={lockLinks} className="min-h-screen bg-[#F9F9F8]">{children}<DuplicatePropertyDialog /></div>;
  }

  return (
    <div className="flex h-screen bg-[#F9F9F8]">
      {/* Viewing Franchise Banner */}
      {isViewingFranchise && (
        <div data-testid="viewing-franchise-banner" className="fixed top-0 left-0 right-0 z-50 bg-amber-500 text-white text-center py-1.5 text-xs font-bold tracking-wide flex items-center justify-center gap-2">
          <Eye size={14} /> READ-ONLY: Viewing franchise data &middot;
          <button onClick={async () => { try { await api.post('/franchises/exit-view'); window.location.reload(); } catch {} }} className="underline hover:no-underline">Exit View</button>
        </div>
      )}
      {/* Sidebar */}
      <aside data-testid="app-sidebar" className={`fixed inset-y-0 left-0 z-40 w-60 bg-white border-r border-[#E2E2DF] transform transition-transform duration-200 lg:relative lg:translate-x-0 ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'} ${isViewingFranchise ? 'pt-8' : ''}`}>
        <div className="flex items-center gap-3 px-5 py-4 border-b border-[#E2E2DF]">
          <div className="w-9 h-9 flex items-center justify-center overflow-hidden"><img src="/rtr-logo.png" alt="RTR" className="w-full h-full object-contain" /></div>
          <div className="flex-1 min-w-0">
            <h1 className="text-base font-extrabold tracking-tight text-slate-900 font-[Manrope]">RTR CRM</h1>
            <p className="text-[10px] text-slate-500 -mt-0.5">{isFranchise ? user?.franchise_name : 'Production system'}</p>
          </div>
          <button className="lg:hidden ml-auto text-slate-400" onClick={() => setSidebarOpen(false)}><X size={20} /></button>
        </div>
        <nav className="p-3 space-y-0.5 overflow-y-auto" style={{ maxHeight: 'calc(100vh - 140px)' }}>
          {NAV_ITEMS.filter(item => {
            if (item.adminOnly && user?.role !== 'admin') return false;
            if (item.masterOnly && isFranchise) return false;
            return true;
          }).map((item, idx) => {
            if (item.type === 'divider') {
              return <div key={`div-${idx}`} className="pt-3 pb-1 px-3"><span className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-400">{item.label}</span></div>;
            }
            const [itemPathname] = item.path.split('?');
            const itemHasQuery = item.path.includes('?');
            let active;
            if (itemHasQuery) {
              // Query-string entries (e.g. /report-setup?tab=pdf_styling) match exactly
              active = fullPath === item.path;
            } else {
              const pathnameMatches = currentPath === itemPathname || (itemPathname !== '/' && currentPath.startsWith(itemPathname + '/'));
              // Don't highlight a bare path if a sibling with the exact query is matching the URL
              const hasMoreSpecificSibling = NAV_ITEMS.some(it => it !== item && it.path && it.path !== '/' && it.path.startsWith(itemPathname + '?') && it.path === fullPath);
              active = pathnameMatches && !hasMoreSpecificSibling;
            }
            return (
              <button key={item.path} data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`} onClick={() => { navigate(item.path); setSidebarOpen(false); }} className={`w-full flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium transition-all duration-200 ${active ? 'bg-[#D35400]/[0.08] text-[#D35400] nav-active-accent font-semibold' : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'}`}>
                <item.icon size={17} strokeWidth={active ? 2 : 1.5} />
                {item.label}
              </button>
            );
          })}
        </nav>
        <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-[#E2E2DF]">
          <div className="flex items-center gap-3">
            <button
              data-testid="sidebar-profile-button"
              onClick={() => { navigate('/profile'); setSidebarOpen(false); }}
              className="flex items-center gap-3 flex-1 min-w-0 text-left hover:bg-slate-50 rounded-md -mx-1 px-1 py-1 transition-colors"
              title="My Profile — change email or password"
            >
              <div className="w-8 h-8 rounded-full bg-slate-200 flex items-center justify-center text-xs font-bold text-slate-600">{(user?.name || 'U')[0]}</div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-slate-800 truncate">{user?.name}</p>
                <p className="text-xs text-slate-500 capitalize">{user?.role?.replace('_', ' ')}</p>
              </div>
            </button>
            <button data-testid="logout-button" onClick={logout} className="text-slate-400 hover:text-red-500 transition-colors"><LogOut size={16} /></button>
          </div>
        </div>
      </aside>
      {sidebarOpen && <div className="fixed inset-0 bg-black/30 z-30 lg:hidden" onClick={() => setSidebarOpen(false)} />}
      {/* Main */}
      <div className="flex-1 flex flex-col min-w-0">
        <header className="flex items-center justify-between px-4 lg:px-6 py-2.5 bg-white border-b border-[#E2E2DF] flex-shrink-0 gap-3">
          <div className="flex items-center gap-1 flex-shrink-0">
            <button className="lg:hidden text-slate-500 p-1 mr-1" onClick={() => setSidebarOpen(true)}><Menu size={22} /></button>
            <button data-testid="nav-back" onClick={() => navigate(-1)} className="p-1.5 rounded-md text-slate-400 hover:text-slate-700 hover:bg-slate-100 transition-colors" title="Go back"><ChevronLeft size={18} strokeWidth={2} /></button>
            <button data-testid="nav-forward" onClick={() => navigate(1)} className="p-1.5 rounded-md text-slate-400 hover:text-slate-700 hover:bg-slate-100 transition-colors" title="Go forward"><ChevronRight size={18} strokeWidth={2} /></button>
          </div>
          {/* Stylised chrome wordmark — replaces the legacy "Search clients,
              properties, appraisals…" trigger button as the centrepiece of
              the top header. Acts as a click target back to the dashboard
              and serves as the recognisable brand mark on every page. */}
          <button
            data-testid="topbar-wordmark"
            onClick={() => navigate('/')}
            title="Roofs To Remedy Ltd — go to Dashboard"
            className="flex-1 flex items-center justify-center min-w-0 group py-1"
          >
            <img
              src="/rtr-wordmark.png"
              alt="Roofs To Remedy Ltd"
              className="block h-10 lg:h-14 w-auto max-w-[440px] lg:max-w-[560px] object-contain transition-transform duration-200 group-hover:scale-[1.03]"
            />
          </button>
          <div className="flex items-center gap-1 flex-shrink-0">
            {/* Search relocated from centre — compact button keeps just
                enough placeholder visible (first 3 words) so it still
                reads as a search field. ⌘K still opens it instantly. */}
            <button
              data-testid="global-search-trigger"
              onClick={() => setSearchOpen(true)}
              title="Search clients, properties, appraisals, leads, suppliers… (⌘K)"
              className="flex items-center gap-2 px-3 py-1.5 border border-[#E2E2DF] rounded-md text-sm text-slate-400 hover:border-slate-300 hover:text-slate-700 transition-colors max-w-[260px]"
            >
              <Search size={14} className="flex-shrink-0" />
              <span className="hidden md:inline whitespace-nowrap">Search clients, properties…</span>
              <span className="md:hidden">Search…</span>
              <kbd className="hidden lg:inline-flex flex-shrink-0 text-[10px] px-1 py-0 bg-slate-50 rounded text-slate-400 border border-[#E2E2DF]"><Command size={9} className="inline" />K</kbd>
            </button>
            <button data-testid="help-button" onClick={() => setHelpOpen(true)} className="p-1.5 rounded-md text-slate-400 hover:text-[#D35400] hover:bg-orange-50 transition-colors" title="Page Help">
              <HelpCircle size={18} strokeWidth={1.5} />
            </button>
            <NotificationBell />
            <WalkthroughRecorderBanner />
            <DriveRenamePrompt />
          </div>
        </header>
        <main className="flex-1 overflow-y-auto p-4 lg:p-6 relative">
          {/* House-truck mascot — top-right corner of every page. Sits at
              the same vertical level as each page's H1 title. Action
              buttons (e.g. "+ New Client") get a reserved right-margin on
              the children wrapper so they never collide with the mascot.

              Easter-egg: subtle "drive forward" hover animation (translate
              + scale) — Trevor's hand-drawn brand vibe, not corporate. */}
          <img
            src="/rtr-house-truck-icon.png"
            alt="Roof Tiling Remedies"
            data-testid="page-mascot"
            title="Roof Tiling Remedies — click to go home"
            onClick={() => navigate('/')}
            className="hidden md:block absolute top-2 right-3 lg:right-4 w-16 lg:w-20 h-auto object-contain select-none opacity-95 drop-shadow-sm z-20 cursor-pointer rtr-mascot-idle"
          />
          {/* Right-padding reserved so page H1 + action buttons clear the
              mascot (matches mascot width 80px + 16px gap). Pages that
              don't have an H1 row (e.g. the PDF viewer) still get the
              padding, which is fine — it just trims a sliver. */}
          <div className="relative z-10 md:pr-24 lg:pr-28">{children}</div>
        </main>
      </div>
      <DuplicatePropertyDialog />
      {/* Help Panel */}
      <HelpPanel open={helpOpen} onClose={() => setHelpOpen(false)} />
      {/* Search Modal */}
      {searchOpen && (
        <div className="fixed inset-0 z-50 flex items-start justify-center pt-[15vh] p-4" onClick={() => setSearchOpen(false)}>
          <div className="absolute inset-0 bg-black/40" />
          <div data-testid="global-search-modal" className="relative w-full max-w-lg bg-white rounded-lg shadow-2xl border border-[#E2E2DF] overflow-hidden" onClick={e => e.stopPropagation()}>
            <div className="flex items-center gap-3 px-4 py-3 border-b border-[#E2E2DF]">
              <Search size={18} className="text-slate-400 flex-shrink-0" />
              <input ref={searchRef} data-testid="global-search-input" value={searchQuery} onChange={e => doSearch(e.target.value)} placeholder="Search clients, properties, appraisals, leads..." className="flex-1 text-sm outline-none bg-transparent" autoFocus />
              <kbd className="text-xs px-1.5 py-0.5 bg-slate-100 rounded text-slate-400 border border-[#E2E2DF]">ESC</kbd>
            </div>
            {searchResults.length > 0 && (
              <div className="max-h-80 overflow-y-auto p-2">
                {searchResults.map((r, i) => {
                  const Icon = typeIcons[r.type] || Briefcase;
                  return (
                    <button key={i} data-testid={`search-result-${i}`} onClick={() => goToResult(r)} className="w-full flex items-center gap-3 px-3 py-2.5 rounded-md hover:bg-slate-50 transition-colors text-left">
                      <Icon size={16} className="text-slate-400 flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-slate-800 truncate">{r.title}</p>
                        <p className="text-xs text-slate-500 truncate">{r.subtitle}</p>
                      </div>
                      <span className="text-[10px] uppercase tracking-wider text-slate-400 font-bold">{r.type}</span>
                    </button>
                  );
                })}
              </div>
            )}
            {searchQuery.length >= 2 && searchResults.length === 0 && (
              <div className="p-6 text-center text-sm text-slate-400">No results found</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
